/**
 * WellMap — a real Leaflet map of the well register. Field-user-first: a clean
 * spatial view with status-coloured pins, no data-architecture jargon.
 *
 * Offline-safe: OSM raster tiles are attempted, but the map renders and pins
 * are placed even with no network (MSW/standalone mode) because the base layer
 * is a plain styled pane — tiles simply fail-soft to the background colour. No
 * external asset is *required* for the map to be usable.
 */
import { useEffect, useMemo, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { WellView, WellStatus } from "@api/types";

const STATUS_COLOR: Record<WellStatus, string> = {
  drilling: "#f5a524",
  completion: "#d6a531",
  producing: "#3fb950",
  suspended: "#8a95a3",
};
const UNKNOWN_COLOR = "#63707e";

function pinIcon(color: string, selected: boolean): L.DivIcon {
  const size = selected ? 20 : 14;
  const ring = selected
    ? `box-shadow:0 0 0 5px ${color}33,0 2px 6px rgba(0,0,0,.5);`
    : "box-shadow:0 2px 5px rgba(0,0,0,.5);";
  return L.divIcon({
    className: "well-pin",
    html: `<span style="display:block;width:${size}px;height:${size}px;border-radius:50% 50% 50% 0;background:${color};border:2px solid #0e1116;transform:rotate(-45deg);${ring}"></span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
  });
}

export interface WellMapProps {
  wells: WellView[];
  selectedId?: string | null;
  onSelect?: (w: WellView) => void;
  ariaLabel?: string;
}

export function WellMap({ wells, selectedId, onSelect, ariaLabel }: WellMapProps) {
  const elRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);

  const located = useMemo(
    () => wells.filter((w) => w.lat != null && w.lon != null),
    [wells],
  );

  // Init once.
  useEffect(() => {
    if (!elRef.current || mapRef.current) return;
    const map = L.map(elRef.current, {
      center: [10, -60],
      zoom: 3,
      zoomControl: true,
      attributionControl: false,
    });
    // OSM tiles when online; fail-soft (background shows through) when offline.
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 18,
      crossOrigin: true,
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  // Redraw markers when the well set / selection changes.
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const bounds: L.LatLngExpression[] = [];
    for (const w of located) {
      const color =
        w.status && w.status in STATUS_COLOR ? STATUS_COLOR[w.status] : UNKNOWN_COLOR;
      const marker = L.marker([w.lat as number, w.lon as number], {
        icon: pinIcon(color, w.id === selectedId),
        title: w.commonName,
        keyboard: true,
        alt: w.commonName,
      });
      marker.bindTooltip(w.commonName, { direction: "top", offset: [0, -14] });
      if (onSelect) marker.on("click", () => onSelect(w));
      marker.addTo(layer);
      bounds.push([w.lat as number, w.lon as number]);
    }
    if (bounds.length === 1) {
      map.setView(bounds[0] as L.LatLngExpression, 8);
    } else if (bounds.length > 1) {
      map.fitBounds(L.latLngBounds(bounds).pad(0.25), { maxZoom: 9 });
    }
  }, [located, selectedId, onSelect]);

  return (
    <div
      ref={elRef}
      className="well-map"
      role="region"
      aria-label={ariaLabel ?? "Well location map"}
    />
  );
}
